import React, { useRef, useEffect, useState, useMemo } from "react";
import "./GanttComponent.scss";

const DAYS_IN_MONTH = {
  1: 31, 2: 31, 3: 31, 4: 31, 5: 31, 6: 31,
  7: 30, 8: 30, 9: 30, 10: 30, 11: 30, 12: 29,
};

const parseJalali = (dateStr) => {
  const [y, m, d] = dateStr.split("/").map(Number);
  return { y, m, d };
};

const addDays = (dateStr, days) => {
  const { y, m, d } = parseJalali(dateStr);
  let newD = d + days;
  let newM = m;
  let newY = y;

  while (newD > DAYS_IN_MONTH[newM]) {
    newD -= DAYS_IN_MONTH[newM];
    newM++;
    if (newM > 12) {
      newM = 1;
      newY++;
    }
  }

  return `${newY}/${String(newM).padStart(2, "0")}/${String(newD).padStart(2, "0")}`;
};

const getDayDiff = (base, target) => {
  let diff = 0;
  let current = base;
  while (current !== target) {
    diff++;
    current = addDays(current, 1);
    if (diff > 1000) break;
  }
  return diff;
};

const TASK_CONFIG = [
  { key: "sabt_ta_tahvil", title: "ثبت تا تحویل", color: "#88AB0F" },
  { key: "sabt_ta_amade", title: "ثبت تا آماده سازی", color: "#DC2E51" },
  { key: "sabt_ta_takmil", title: "ثبت تا تکمیل سفارش", color: "#FE5D26" },
  { key: "takmil_ta_amade", title: "تکمیل تا آماده سازی", color: "#01C5C4" },
  { key: "takmil_ta_bar", title: "تکمیل تا بارگیری", color: "#4AA96C" },
  { key: "tahvil_ta_bar", title: "بارگیری تا تحویل", color: "#998CEB" },
];

const GanttComponent = ({ data }) => {
  const timelineRef = useRef(null);
  const [cellWidth, setCellWidth] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  const validTasks = useMemo(() => {
    return TASK_CONFIG.map((cfg) => {
      const item = data[cfg.key];
      if (
        !item ||
        !item.start_date ||
        !item.end_date ||
        item.start_date.trim() === "" ||
        item.end_date.trim() === ""
      ) {
        return null;
      }

      const start = item.start_date;
      const end = item.end_date;
      const correctedStart = start <= end ? start : end;
      const correctedEnd = start <= end ? end : start;

      const label = `${item.day} روز${item.hours > 0 ? ` و ${item.hours} ساعت` : ""}`;

      return {
        ...cfg,
        startDate: correctedStart,
        endDate: correctedEnd,
        label,
      };
    }).filter(Boolean);
  }, [data]);

  const { minStart, maxEnd } = useMemo(() => {
    if (validTasks.length === 0) {
      return { minStart: "", maxEnd: "" };
    }

    let minStart = validTasks[0].startDate;
    let maxEnd = validTasks[0].endDate;

    validTasks.forEach((t) => {
      if (t.startDate < minStart) minStart = t.startDate;
      if (t.endDate > maxEnd) maxEnd = t.endDate;
    });

    return { minStart, maxEnd };
  }, [validTasks]);

  const displayDates = useMemo(() => {
    if (!minStart || !maxEnd) return [];

    const dates = [];
    let current = minStart;

    while (current <= maxEnd) {
      dates.push(current);
      current = addDays(current, 1);
    }

    return dates;
  }, [minStart, maxEnd]);

  const calculateCellWidth = () => {
    if (timelineRef.current && displayDates.length > 0) {
      const containerWidth = timelineRef.current.offsetWidth;
      const totalIntervals = displayDates.length - 1;
      const width = totalIntervals > 0 ? containerWidth / totalIntervals : 0;
      setCellWidth(width);
    }
  };

  useEffect(() => {
    if (displayDates.length > 0) {
      calculateCellWidth();
      const onResize = () => calculateCellWidth();
      window.addEventListener("resize", onResize);
      return () => window.removeEventListener("resize", onResize);
    }
  }, [displayDates.length]);

  useEffect(() => {
    if (validTasks.length > 0) {
      const timer = setTimeout(() => setIsVisible(true), 50);
      return () => clearTimeout(timer);
    }
  }, [validTasks.length]);

  const labelStep = useMemo(() => {
    if (cellWidth === 0) return 1;
    const minLabelWidth = 50;
    if (cellWidth >= minLabelWidth) return 1;

    const step = Math.ceil(minLabelWidth / cellWidth);
    return Math.max(1, step);
  }, [cellWidth]);

  const shouldShowLabels = cellWidth > 0;

  if (validTasks.length === 0) {
    return (
      <div className="gantt-wrapper">
        <div className="gantt-empty">هیچ داده معتبری برای نمایش وجود ندارد.</div>
      </div>
    );
  }

  return (
    <div className="gantt-wrapper">
      <div className={`gantt-container ${isVisible ? "animate-in" : ""}`}>
        <div className="gantt-timeline" ref={timelineRef}>
     
          {Array.from({ length: displayDates.length }).map((_, i) => (
            <div
              key={`vline-${i}`}
              className="gantt-vertical-line"
              style={{
                left: `${i * cellWidth}px`,
                animationDelay: `${i * 0.03}s`,
              }}
            />
          ))}

          {shouldShowLabels &&
            displayDates
              .filter((_, i) => i % labelStep === 0)
              .map((date, filteredIndex) => {
                const originalIndex = filteredIndex * labelStep;
                return (
                  <div
                    key={`label-${originalIndex}`}
                    className="gantt-date-label"
                    style={{
                      left: `${originalIndex * cellWidth}px`,
                      transform: "translateX(-50%)",
                      animationDelay: `${originalIndex * 0.02}s`,
                    }}
                  >
                    {date.slice(5)}
                  </div>
                );
              })}
        </div>

        <div className="gantt-bars">
          {validTasks.map((task, index) => {
            const startOffset = getDayDiff(minStart, task.startDate);
            const endOffset = getDayDiff(minStart, task.endDate);

            const left = startOffset * cellWidth;
            const width = (endOffset - startOffset) * cellWidth;

            return (
              <div
                key={task.key}
                className="gantt-bar"
                style={{
                  left: `${left}px`,
                  width: `${width}px`,
                  top: `${index * 55}px`,
                  backgroundColor: task.color,
                  animationDelay: `${index * 0.1 + 0.2}s`,
                }}
              >
                <span className="bar-label bar-label-inside">
                  {task.title}
                  <br />
                  <small>{task.label}</small>
                </span>
              </div>
            );
          })}
        </div>
      </div>

      <div className={`legend-container ${isVisible ? "animate-in" : ""}`}>
        {validTasks.map((task, i) => (
          <div
            key={task.key}
            className="legend-item"
            style={{ animationDelay: `${i * 0.05 + 0.5}s` }}
          >
            <div
              className="legend-color"
              style={{ backgroundColor: task.color }}
            />
            <span className="legend-title">{task.title}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default GanttComponent;